/**
 * The door to the platform: a client's invitation link (owner, 2026-10-29).
 * Step 1 says who invited and what Skryptos is; step 2 opens the account and
 * signs in, and the rest of the way continues inside the cabinet.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { Button } from "@/design-system/Button";
import { useSignIn } from "./useSession";
import { joinByInvitation, readInvitation } from "@/shared/api";
import { clearViewAs } from "@/shared/session/viewAs";

import { Steps } from "@/shared/onboarding/Steps";

const MIN_LENGTH = 8;
const FIELD =
  "mt-1 block w-full rounded-md border border-border-subtle bg-background-base px-3 py-2 text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring";

/** The code at the end of /join/<code>; empty when the path carries none. */
export function joinCode(pathname: string): string {
  const match = /^\/join\/([A-Za-z0-9]{10})\/?$/.exec(pathname);
  return match?.[1] ?? "";
}

function Shell({ step, children }: { readonly step: number; readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <main className="flex min-h-dvh items-start justify-center bg-background-base px-4 py-10">
      <section className="flex w-full max-w-lg flex-col gap-6 rounded-lg border border-border-subtle bg-surface-default p-6" data-testid="join">
        <h1 className="text-lg font-semibold text-text-primary">Подключение к Skryptos</h1>
        <Steps current={step} />
        {children}
      </section>
    </main>
  );
}

export function JoinPage(): React.JSX.Element {
  const code = joinCode(useLocation().pathname);
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [accepted, setAccepted] = useState(false);
  const signIn = useSignIn();
  const navigate = useNavigate();
  const invitation = useQuery({
    queryKey: ["invitation", code],
    enabled: code !== "",
    retry: false,
    queryFn: async () => {
      const { data } = await readInvitation({ path: { code } });
      if (data === undefined) throw new Error("no invitation");
      return data;
    },
  });
  const join = useMutation({
    mutationFn: async () => {
      clearViewAs();
      const { response } = await joinByInvitation({
        path: { code },
        body: { display_name: name.trim(), login: login.trim(), password },
      });
      if (response?.status === 409) throw new Error("taken");
      if (response?.status === 422) throw new Error("input");
      if (response?.status === 429) throw new Error("limit");
      if (response?.ok !== true) throw new Error("refused");
    },
    // Signed in at once: the next steps live in the cabinet.
    onSuccess: () => {
      void navigate("/app/start", { replace: true });
      signIn.submit({ login: login.trim().toLowerCase(), tenantId: null, accountId: null, password });
    },
  });

  if (code === "" || invitation.isError) {
    return (
      <Shell step={0}>
        <p role="alert" className="text-body-main text-status-negative">
          Ссылка-приглашение недействительна. Попросите того, кто вас пригласил, прислать её ещё раз.
        </p>
      </Shell>
    );
  }
  if (invitation.data === undefined) {
    return <Shell step={0}><p role="status" className="text-body-main text-text-secondary">Проверяю приглашение…</p></Shell>;
  }
  if (step === 0) {
    return (
      <Shell step={0}>
        <div className="flex flex-col gap-3 text-body-main text-text-secondary" data-testid="join-invitation">
          <p className="text-heading-sm text-text-primary">Вас пригласил {invitation.data.inviter}</p>
          <p>Skryptos — торговый бот для Bybit. Он сам открывает и закрывает сделки на вашем счёте по стратегии платформы; деньги всегда остаются на вашей бирже.</p>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            <li>Комиссия — 20 % только с прибыли. Нет прибыли — нет комиссии.</li>
            <li>20 USDT приветственного бонуса на оплату комиссии.</li>
            <li>Перед каждой новой сделкой бот проверяет, что счёт выдержит падение рынка на 50 %.</li>
          </ul>
          <p>Подключение займёт около 10 минут: аккаунт, ключ Bybit, уведомления в Telegram и запуск.</p>
        </div>
        <Button type="button" variant="primary" onClick={() => { setStep(1); }}>Продолжить</Button>
      </Shell>
    );
  }
  const ready = name.trim() !== "" && login.trim() !== "" && password.length >= MIN_LENGTH && password === confirmation && accepted;
  return (
    <Shell step={1}>
      <form
        className="flex flex-col gap-3"
        data-testid="join-account"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready && !join.isPending) join.mutate();
        }}
      >
        <label className="text-sm text-text-secondary">
          Ваше имя
          <input className={FIELD} required maxLength={128} autoComplete="name" value={name} onChange={(event) => { setName(event.target.value); }} />
        </label>
        <label className="text-sm text-text-secondary">
          Email — это ваш логин
          <input className={FIELD} type="email" required maxLength={128} autoComplete="username" value={login} onChange={(event) => { setLogin(event.target.value); }} />
        </label>
        <label className="text-sm text-text-secondary">
          Пароль
          <input className={FIELD} type="password" required minLength={MIN_LENGTH} autoComplete="new-password" value={password} onChange={(event) => { setPassword(event.target.value); }} />
        </label>
        <p className={`-mt-2 text-helper-sm ${password !== "" && password.length < MIN_LENGTH ? "text-status-warning" : "text-text-muted"}`}>
          Не короче {MIN_LENGTH} символов.
        </p>
        <label className="text-sm text-text-secondary">
          Повторите пароль
          <input className={FIELD} type="password" required autoComplete="new-password" value={confirmation} onChange={(event) => { setConfirmation(event.target.value); }} />
        </label>
        {confirmation !== "" && password !== confirmation && <p role="alert" className="text-sm text-status-negative">Пароли не совпадают.</p>}
        <label className="flex cursor-pointer items-start gap-2 text-sm text-text-secondary">
          <input type="checkbox" className="mt-1 cursor-pointer" checked={accepted} onChange={(event) => { setAccepted(event.target.checked); }} />
          <span>Я понимаю, что торговля на бирже связана с риском потери средств, и принимаю условия платформы.</span>
        </label>
        {join.isError && (
          <p role="alert" className="text-sm text-status-negative">
            {join.error.message === "taken"
              ? "Этот email уже зарегистрирован или недоступен. Войдите или используйте другой."
              : join.error.message === "input"
                ? `Проверьте имя и email; пароль — не короче ${String(MIN_LENGTH)} символов.`
                : join.error.message === "limit"
                  ? "Сегодня регистрации по приглашениям закончились. Попробуйте завтра."
                  : "Регистрация не прошла. Попробуйте ещё раз."}
          </p>
        )}
        {signIn.failure !== null && <p role="alert" className="text-sm text-status-negative">Аккаунт создан, но войти не получилось. Войдите со своим email и паролем.</p>}
        <div className="flex gap-2">
          <Button type="button" onClick={() => { setStep(0); }}>Назад</Button>
          <Button type="submit" variant="primary" disabled={!ready || join.isPending || signIn.pending}>
            {join.isPending || signIn.pending ? "Создаю…" : "Создать аккаунт"}
          </Button>
        </div>
      </form>
    </Shell>
  );
}
